import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as PercentLogo } from "../../assets/images/percentMapLogo.svg";
import { ReactComponent as HiveIcon } from "../../assets/images/hive-icon.svg";
import signUpImg from "../../assets/images/screenshots/sign_up.jpeg";
import nftFrontImg from "../../assets/images/screenshots/nft_front.jpeg";
import nftBackImg from "../../assets/images/screenshots/nft_back.jpeg";
import rewardClaim1EnImg from "../../assets/images/screenshots/reward_claim_1_en.jpeg";
import rewardClaim1KoImg from "../../assets/images/screenshots/reward_claim_1_ko.jpeg";
import rewardClaim2EnImg from "../../assets/images/screenshots/reward_claim_2_en.jpeg";
import rewardClaim2KoImg from "../../assets/images/screenshots/reward_claim_2_ko.jpeg";
import feedHomeEnImg from "../../assets/images/screenshots/feed_home_en.png";
import feedHomeKoImg from "../../assets/images/screenshots/feed_home_ko.png";
import hiveSettingEnImg from "../../assets/images/screenshots/hive_account_setting_en.jpeg";
import hiveSettingKoImg from "../../assets/images/screenshots/hive_account_setting_ko.jpeg";
import levelStoneImg from "../../assets/images/nft/level_stone.png";
import levelSilverImg from "../../assets/images/nft/level_silver.png";
import levelGoldImg from "../../assets/images/nft/level_gold.png";
import levelRubyImg from "../../assets/images/nft/level_ruby.png";
import levelDiamondImg from "../../assets/images/nft/level_diamond.png";
import nftMapImg from "../../assets/images/nft/nft_map.jpeg";
import {
    type Lang,
    navItemsByLang,
    i18n,
    HeroDesc,
    WitnessDesc,
    CompetitionDesc,
    RarityDesc,
    getFaqItems,
} from "./i18n";

type Props = {
    isSignIn?: boolean;
};

function classNames(...arr: Array<string | false | undefined | null>) {
    return arr.filter(Boolean).join(" ");
}

/** ===== Scroll Spy (active section) ===== */
function useActiveSection(ids: string[]) {
    const [active, setActive] = useState<string>(ids[0] ?? "features");

    useEffect(() => {
        const els = ids
            .map((id) => document.getElementById(id))
            .filter(Boolean) as HTMLElement[];
        if (!els.length) return;

        const io = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort(
                        (a, b) =>
                            (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0)
                    );
                if (visible[0]?.target?.id) setActive(visible[0].target.id);
            },
            {
                root: null,
                rootMargin: "-20% 0px -45% 0px",
                threshold: [0, 0.05, 0.1, 0.25, 0.5],
            }
        );

        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [ids]);

    return active;
}

/** ===== Scroll Progress across sections ===== */
function useSectionProgress(ids: string[]) {
    const [progress, setProgress] = useState(0); // 0 ~ 1

    useEffect(() => {
        const onScroll = () => {
            const tops = ids
                .map((id) => document.getElementById(id))
                .filter(Boolean)
                .map((el) => (el as HTMLElement).getBoundingClientRect().top + window.scrollY);

            if (!tops.length) return;

            const start = tops[0];
            const endEl = document.getElementById(ids[ids.length - 1]);
            const end =
                (endEl?.getBoundingClientRect().top ?? 0) +
                window.scrollY +
                (endEl?.offsetHeight ?? 0);

            const y = window.scrollY + 120; // header offset
            const raw = (y - start) / Math.max(1, end - start);
            const clamped = Math.min(1, Math.max(0, raw));
            setProgress(clamped);
        };

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [ids]);

    return progress;
}

/** ===== Page background: clean gray/white gradient ===== */
function PageBackdrop() {
    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-zinc-950" />
            <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(255,255,255,0.08),transparent_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_10%_20%,rgba(255,255,255,0.05),transparent_62%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(900px_500px_at_90%_35%,rgba(255,255,255,0.04),transparent_60%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.0),rgba(0,0,0,0.7))]" />
        </div>
    );
}

/** ===== Section background: gray/white gradient only ===== */
function SectionBackdrop() {
    return (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_50%_0%,rgba(255,255,255,0.06),transparent_62%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.02),transparent_65%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(800px_420px_at_50%_110%,rgba(255,255,255,0.02),transparent_60%)]" />
        </div>
    );
}

/** ===== TrioGlow: 제한된 시안/그레이 톤 (섹션 통일) ===== */
function TrioGlow({ className }: { className?: string }) {
    return (
        <div
            aria-hidden
            className={classNames(
                "pointer-events-none absolute inset-0 -z-10 overflow-hidden",
                className
            )}
        >
            {/* top (cyan-ish, subtle) */}
            <div className="absolute -top-28 left-1/2 h-[340px] w-[340px] -translate-x-1/2 rounded-full bg-cyan-400/12 blur-[100px]" />
            {/* left (gray-ish) */}
            <div className="absolute top-20 -left-24 h-[360px] w-[360px] rounded-full bg-white/6 blur-[120px]" />
            {/* right (cyan/gray mix) */}
            <div className="absolute top-28 -right-24 h-[360px] w-[360px] rounded-full bg-cyan-500/8 blur-[120px]" />
        </div>
    );
}

/** ===== Unified hover lift (Apple-like) =====
 * - translateY -2
 * - shadow 강화
 * - border 약간 강조
 */
const cardHover =
    "transition will-change-transform hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-25px_rgba(0,0,0,0.65)] hover:border-white/20";

function Badge({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
            {children}
        </span>
    );
}

function SectionTitle({
    kicker,
    title,
    subtitle,
}: {
    kicker?: string;
    title: string;
    subtitle?: string;
}) {
    return (
        <div className="mx-auto max-w-3xl text-center">
            {kicker ? (
                <div className="text-[11px] font-medium tracking-[0.22em] text-white/50">
                    {kicker}
                </div>
            ) : null}
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white md:text-3xl">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                    {subtitle}
                </p>
            ) : null}
        </div>
    );
}

function PhoneMock({ src, alt = "Percent Map 앱 화면", className }: { src: string; alt?: string; className?: string }) {
    return (
        <div className={classNames("relative w-full", className)}>
            {/* 폰 외곽 프레임 */}
            <div className="relative rounded-[44px] border border-white/20 bg-zinc-900 p-[3px] shadow-[0_32px_80px_-20px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.12)]">
                {/* 내부 베젤 */}
                <div className="relative overflow-hidden rounded-[41px] bg-zinc-950">
                    {/* 노치(Dynamic Island 스타일) */}
                    <div className="absolute left-1/2 top-3 z-10 h-[28px] w-[88px] -translate-x-1/2 rounded-full bg-zinc-950 border border-white/10" />
                    {/* 스크린샷 이미지 */}
                    <img
                        src={src}
                        alt={alt}
                        className="block w-full object-cover object-top"
                        draggable={false}
                    />
                    {/* 화면 상단 반사광 */}
                    <div className="pointer-events-none absolute inset-0 rounded-[41px] bg-[linear-gradient(160deg,rgba(255,255,255,0.07)_0%,transparent_40%)]" />
                </div>
            </div>
            {/* 사이드 버튼들 */}
            <div className="absolute -right-[5px] top-[90px] h-[36px] w-[4px] rounded-r-full bg-zinc-700" />
            <div className="absolute -left-[5px] top-[80px] h-[28px] w-[4px] rounded-l-full bg-zinc-700" />
            <div className="absolute -left-[5px] top-[120px] h-[52px] w-[4px] rounded-l-full bg-zinc-700" />
            <div className="absolute -left-[5px] top-[184px] h-[52px] w-[4px] rounded-l-full bg-zinc-700" />
        </div>
    );
}

/** ===== NAV link with unified underline/active ===== */
function NavLink({
    label,
    href,
    active,
    onClick,
}: {
    label: string;
    href: string;
    active: boolean;
    onClick?: () => void;
}) {
    return (
        <a
            href={href}
            onClick={onClick}
            className={classNames(
                "relative text-sm text-white/70 hover:text-white transition",
                "after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-full after:rounded-full after:bg-white/70 after:opacity-0 after:transition",
                "hover:after:opacity-100",
                active && "text-white after:opacity-100"
            )}
        >
            {label}
        </a>
    );
}

function Hamburger({
    open,
    onClick,
}: {
    open: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10 md:hidden"
            aria-label="Open menu"
            aria-expanded={open}
        >
            <span className="relative block h-4 w-5">
                <span
                    className={classNames(
                        "absolute left-0 top-0 h-[2px] w-5 rounded bg-white/80 transition",
                        open && "translate-y-[7px] rotate-45"
                    )}
                />
                <span
                    className={classNames(
                        "absolute left-0 top-[7px] h-[2px] w-5 rounded bg-white/80 transition",
                        open && "opacity-0"
                    )}
                />
                <span
                    className={classNames(
                        "absolute left-0 top-[14px] h-[2px] w-5 rounded bg-white/80 transition",
                        open && "-translate-y-[7px] -rotate-45"
                    )}
                />
            </span>
        </button>
    );
}

/** ===== How it works card (with emoji icon) ===== */
function HowCard({
    step,
    title,
    desc,
    icon,
    accentLeft,
}: {
    step: number;
    title: string;
    desc: string;
    icon: string;
    accentLeft?: boolean;
}) {
    return (
        <div
            className={classNames(
                "relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/30 p-5 backdrop-blur",
                cardHover
            )}
        >
            <TrioGlow />
            <div
                aria-hidden
                className={classNames(
                    "pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-white/12",
                    accentLeft && "bg-white/40"
                )}
            />
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-zinc-950/30 text-lg">
                    {icon}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white/80">
                            {step}
                        </span>
                        <div className="text-sm font-semibold text-white/90">{title}</div>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-white/60">{desc}</p>
                </div>
            </div>
        </div>
    );
}

/** ===== FAQ item with +/− & rotate ===== */
function FaqItem({ q, a }: { q: string; a: React.ReactNode }) {
    const ref = useRef<HTMLDetailsElement | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const onToggle = () => setOpen(el.open);
        el.addEventListener("toggle", onToggle);
        setOpen(el.open);
        return () => el.removeEventListener("toggle", onToggle);
    }, []);

    return (
        <details
            ref={ref}
            className={classNames(
                "group rounded-2xl border border-white/10 bg-white/5 p-5 open:border-white/15",
                cardHover
            )}
        >
            <TrioGlow />
            <summary className="cursor-pointer list-none select-none">
                <div className="flex items-center justify-between gap-4">
                    <div className="text-sm font-semibold text-white">{q}</div>
                    <div
                        className={classNames(
                            "flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-zinc-950/30 text-white/70",
                            "transition-transform duration-200",
                            open ? "rotate-180" : "rotate-0"
                        )}
                        aria-hidden
                    >
                        <span className="text-sm leading-none">{open ? "−" : "+"}</span>
                    </div>
                </div>
            </summary>
            <div className="mt-3 text-sm leading-relaxed text-white/70">{a}</div>
        </details>
    );
}

export function HomeMainPage({ isSignIn }: Props) {
    const sectionIds = useMemo(
        () => ["features", "witness", "earn", "faq", "contact"],
        []
    );
    const active = useActiveSection(sectionIds);
    const progress = useSectionProgress(sectionIds);

    const [mobileOpen, setMobileOpen] = useState(false);

    // Contact form state
    const [contactForm, setContactForm] = useState({ email: "", subject: "", content: "" });
    const [contactStatus, setContactStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

    const handleContactSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const { email, subject, content } = contactForm;
        if (!email || !subject || !content) {
            alert(t.contact.required);
            return;
        }
        setContactStatus("sending");
        try {
            const res = await fetch("/api/services/contact/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, subject, content }),
            });
            const json = await res.json();
            setContactStatus(json.ok ? "success" : "error");
        } catch {
            setContactStatus("error");
        }
    };
    const [lang, setLang] = useState<Lang>(() => {
        try {
            const stored = localStorage.getItem("pm-lang");
            if (stored === "en" || stored === "ko") return stored;
        } catch { }
        return "en";
    });

    useEffect(() => {
        try { localStorage.setItem("pm-lang", lang); } catch { }
    }, [lang]);

    const t = i18n[lang];
    const navItems = navItemsByLang[lang];

    useEffect(() => {
        if (!mobileOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileOpen]);

    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (!hash) return;
        // RAF ensures the DOM is fully painted before scrolling
        const raf = requestAnimationFrame(() => {
            const el = document.getElementById(hash);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return () => cancelAnimationFrame(raf);
    }, []);

    const activeLabel = useMemo(() => {
        const found = navItems.find((n) => n.href.replace("#", "") === active);
        return found?.label ?? navItems[0]?.label ?? "Features";
    }, [active, navItems]);

    return (
        <div className="relative min-h-screen w-full bg-zinc-950 text-white">
            <PageBackdrop />

            {/* NAV */}
            <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/70 backdrop-blur">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
                    <a href="/" className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                            <PercentLogo className="h-8 w-8" />
                            <div className="font-brand text-2xl font-semibold tracking-tight leading-none">
                                Percent Map
                            </div>
                        </div>
                    </a>

                    {/* Desktop nav */}
                    <nav className="hidden items-center gap-6 md:flex">
                        {navItems.map((it) => {
                            const id = it.href.replace("#", "");
                            return (
                                <NavLink
                                    key={it.href}
                                    href={it.href}
                                    label={it.label}
                                    active={active === id}
                                />
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-2">
                        {/* Language toggle */}
                        <div className="flex items-center rounded-full border border-white/10 bg-white/5 p-0.5 text-xs">
                            <button
                                onClick={() => setLang("ko")}
                                className={classNames(
                                    "rounded-full px-2.5 py-1 transition",
                                    lang === "ko"
                                        ? "bg-white/15 text-white"
                                        : "text-white/50 hover:text-white/70"
                                )}
                            >
                                KO
                            </button>
                            <button
                                onClick={() => setLang("en")}
                                className={classNames(
                                    "rounded-full px-2.5 py-1 transition",
                                    lang === "en"
                                        ? "bg-white/15 text-white"
                                        : "text-white/50 hover:text-white/70"
                                )}
                            >
                                EN
                            </button>
                        </div>

                        {/* Mobile menu */}
                        <Hamburger
                            open={mobileOpen}
                            onClick={() => setMobileOpen((v) => !v)}
                        />
                    </div>
                </div>


                {/* Mobile dropdown overlay */}
                {mobileOpen ? (
                    <div className="md:hidden">
                        <div
                            className="fixed inset-0 z-40 bg-black/60"
                            onClick={() => setMobileOpen(false)}
                            aria-hidden
                        />
                        <div className="fixed left-0 right-0 top-[64px] z-50 border-b border-white/10 bg-zinc-950/95 backdrop-blur">
                            <div className="mx-auto max-w-7xl px-4 py-4">
                                <div className="grid gap-3">
                                    {navItems.map((it) => {
                                        const id = it.href.replace("#", "");
                                        return (
                                            <NavLink
                                                key={it.href}
                                                href={it.href}
                                                label={it.label}
                                                active={active === id}
                                                onClick={() => setMobileOpen(false)}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </header>

            {/* HERO (✅ 롤백: 네가 직전에 준 Hero 디자인으로 복원) */}
            <section className="relative isolate">
                {/* HERO BACKDROP (always behind) */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
                >
                    <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-cyan-500/20 blur-[110px]" />
                    <div className="absolute top-40 -left-40 h-[520px] w-[520px] rounded-full bg-fuchsia-500/10 blur-[130px]" />
                    <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.06),transparent_55%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.0),rgba(0,0,0,0.7))]" />
                </div>

                {/* HERO CONTENT (always on top) */}
                <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 md:grid-cols-2 md:items-center md:px-6 md:py-20">
                    <div>
                        <Badge>{t.hero.badge}</Badge>

                        <h1 className="mt-5 text-4xl font-semibold leading-tight md:text-5xl text-white">
                            {t.hero.titleLine1}{" "}
                            <span className="bg-gradient-to-r from-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">
                                {t.hero.titleHighlight}
                            </span>
                            .
                        </h1>

                        <HeroDesc lang={lang} />

                        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                            {/* App Store */}
                            <a
                                href="https://apps.apple.com/kr/app/percent-map/id6764697770"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-3 hover:bg-white/90 transition"
                            >
                                <svg className="h-6 w-6 shrink-0 text-zinc-950" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                </svg>
                                <div className="text-left">
                                    <div className="text-[10px] leading-none text-zinc-500">Download on the</div>
                                    <div className="text-sm font-semibold leading-tight text-zinc-950">App Store</div>
                                </div>
                            </a>

                            {/* Google Play */}
                            <a
                                href="https://play.google.com/store/apps/details?id=com.percentmap.app"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/8 px-5 py-3 hover:bg-white/12 transition"
                            >
                                <svg className="h-6 w-6 shrink-0" viewBox="0 0 24 24" fill="none">
                                    <path d="M3.18 23.76a2 2 0 0 0 2.07-.22l11.2-6.48-2.53-2.53-10.74 9.23Z" fill="#EA4335"/>
                                    <path d="M20.72 10.27 17.4 8.38 14.6 11l2.8 2.8 3.33-1.9a1.56 1.56 0 0 0 0-2.73-.14-.07-.01-.1Z" fill="#FBBC04"/>
                                    <path d="m3.18.24 10.74 9.23 2.53-2.53L5.25.46A2 2 0 0 0 3.18.24Z" fill="#4285F4"/>
                                    <path d="M3.18.24A1.94 1.94 0 0 0 2 2.02v19.96c0 .74.4 1.38 1 1.72l11.45-11.7L3.18.24Z" fill="#34A853"/>
                                </svg>
                                <div className="text-left">
                                    <div className="text-[10px] leading-none text-white/50">Get it on</div>
                                    <div className="text-sm font-semibold leading-tight text-white">Google Play</div>
                                </div>
                            </a>
                        </div>

                        <div className="mt-6 flex items-center gap-4 text-xs text-white/50">
                            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                                {t.hero.tag1}
                            </div>
                            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                                {t.hero.tag2}
                            </div>
                        </div>
                    </div>

                    <div className="relative mx-auto w-full max-w-[300px] md:max-w-[360px]">
                        {/* 배경 글로우 */}
                        <div className="absolute -inset-8 rounded-[48px] bg-gradient-to-b from-cyan-500/20 via-fuchsia-500/10 to-transparent blur-3xl pointer-events-none" />
                        <PhoneMock src={signUpImg} />
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
                    <SectionTitle
                        kicker={t.features.kicker}
                        title={t.features.title}
                        subtitle={t.features.subtitle}
                    />

                    <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
                        {/* Group 1: 여행하며 기록 + 광고 없는 피드 */}
                        <div className={classNames("relative rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur", cardHover)}>
                            <TrioGlow />
                            <div className="flex flex-col gap-3">
                                <div className={classNames("relative rounded-2xl border border-white/10 bg-zinc-950/30 p-5", cardHover)}>
                                    <h3 className="text-base font-semibold tracking-tight text-white">
                                        {t.features.card1Title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/70">
                                        {t.features.card1Desc}
                                    </p>
                                </div>
                                <div className={classNames("relative rounded-2xl border border-white/10 bg-zinc-950/30 p-5", cardHover)}>
                                    <h3 className="text-base font-semibold tracking-tight text-white">
                                        {t.features.adFreeTitle}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/70">
                                        {t.features.adFreeDesc}
                                    </p>
                                    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                                        {t.features.adFreeTag}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Group 2: 반응이 쌓이면 + Hive 기반 보상 */}
                        <div className={classNames("relative rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur", cardHover)}>
                            <TrioGlow />
                            <div className="flex flex-col gap-3">
                                <div className={classNames("relative rounded-2xl border border-white/10 bg-zinc-950/30 p-5", cardHover)}>
                                    <h3 className="text-base font-semibold tracking-tight text-white">
                                        {t.features.card2Title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/70">
                                        {t.features.card2Desc}
                                    </p>
                                </div>
                                <div className={classNames("relative rounded-2xl border border-white/10 bg-zinc-950/30 p-5", cardHover)}>
                                    <h3 className="text-base font-semibold tracking-tight text-white">
                                        {t.features.hiveTitle}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-white/70">
                                        {t.features.hiveDesc}
                                    </p>
                                    <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#e31337]/30 bg-[#e31337]/10 px-3 py-1">
                                        <HiveIcon className="h-3.5 w-3.5" />
                                        <span className="text-[11px] font-semibold text-[#e31337]/90 leading-none">Hive</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="rewards" className="mt-14">
                        <div className={classNames(
                            "relative rounded-3xl border border-white/10 p-6 backdrop-blur",
                            "bg-gradient-to-br from-cyan-400/[0.06] via-purple-400/[0.04] to-white/[0.04]",
                            "shadow-[0_0_40px_rgba(168,85,247,0.04)]"
                        )}>
                            <div className="flex items-center justify-center gap-2">
                                <h3 className="text-lg uppercase tracking-widest text-white">
                                    {t.rewards.howItWorks}
                                </h3>
                            </div>

                            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
                                <HowCard
                                    step={1}
                                    icon="🗺️"
                                    title={t.rewards.step1Title}
                                    desc={t.rewards.step1Desc}
                                    accentLeft
                                />
                                <HowCard
                                    step={2}
                                    icon="❤️"
                                    title={t.rewards.step2Title}
                                    desc={t.rewards.step2Desc}
                                />
                                <HowCard
                                    step={3}
                                    icon="🏅"
                                    title={t.rewards.step3Title}
                                    desc={t.rewards.step3Desc}
                                />
                            </div>

                            <div className="mt-6 border-t border-white/10 pt-4">
                                <div className="text-xs leading-relaxed text-white/60">
                                    <span className="font-semibold text-white/80">Note.</span>{" "}
                                    {t.rewards.note}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WITNESS */}
            <section id="witness" className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
                    <SectionTitle
                        kicker={t.witness.kicker}
                        title={t.witness.title}
                        subtitle={t.witness.subtitle}
                    />

                    <div
                        className={classNames(
                            "relative mt-10 rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 backdrop-blur",
                            cardHover
                        )}
                    >
                        <TrioGlow />
                        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
                            <div className="max-w-2xl">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                                    {t.witness.selectedBy}
                                </div>

                                <h3 className="mt-4 text-2xl font-semibold tracking-tight">
                                    {t.witness.witnessH3}{" "}
                                    {t.witness.witnessH3Sub && (
                                        <span className="text-white/50 text-base font-medium">
                                            {t.witness.witnessH3Sub}
                                        </span>
                                    )}
                                </h3>

                                <WitnessDesc lang={lang} />

                                <div className="mt-5 flex flex-wrap gap-2 text-xs text-white/60">
                                    <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                                        {t.witness.nftBadgeTag}
                                    </span>
                                    <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-white/45">
                                        {t.witness.selectionNote}
                                    </span>
                                </div>
                            </div>

                            <div className="w-full md:w-[440px] shrink-0">
                                <div className="relative overflow-hidden rounded-2xl border border-white/40 shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_0_32px_-4px_rgba(255,255,255,0.12),0_0_40px_-8px_rgba(103,232,249,0.15)]">
                                    <img
                                        src={nftMapImg}
                                        alt="Witness NFT Map"
                                        className="block w-full object-cover"
                                        draggable={false}
                                    />
                                    {/* 하단 페이드 */}
                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-zinc-900/80 to-transparent" />
                                    {/* 라벨 */}
                                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full border border-white/15 bg-zinc-950/60 px-2.5 py-1 text-[11px] font-semibold text-white/80 backdrop-blur-sm">
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                                        Witness on Map
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 border-t border-white/10 pt-6" />

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 ">
                            {/* NFT 카드 — 왼쪽 */}
                            <div
                                className={classNames(
                                    "rounded-2xl border border-white/10 bg-white/5 p-6",
                                    cardHover
                                )}
                            >
                                <TrioGlow />
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-base font-semibold">{t.witness.nftCardTitle}</div>
                                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/60" />
                                        {t.witness.physicalCard}
                                    </div>
                                </div>

                                <p className="mt-2 text-sm leading-relaxed text-white/70">
                                    {t.witness.nftCardDesc}
                                </p>

                                {/* 계단식 카드 겹침 */}
                                <div className="mt-5 flex items-start">
                                    {/* Front card */}
                                    <div
                                        className={classNames(
                                            "relative z-10 w-[58%] overflow-hidden rounded-2xl border border-white/15 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.8)]",
                                            cardHover
                                        )}
                                    >
                                        <img
                                            src={nftFrontImg}
                                            alt="Witness NFT Front"
                                            className="block w-full object-cover"
                                            draggable={false}
                                        />
                                    </div>
                                    {/* Back card — 오른쪽 + 아래로 계단식 */}
                                    <div
                                        className={classNames(
                                            "relative z-0 w-[48%] -ml-5 mt-10 overflow-hidden rounded-2xl border border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.6)]",
                                            cardHover
                                        )}
                                    >
                                        <img
                                            src={nftBackImg}
                                            alt="Witness NFT Back"
                                            className="block w-full object-cover"
                                            draggable={false}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 경쟁·레벨·유효기간 — 오른쪽 */}
                            <div
                                className={classNames(
                                    "rounded-2xl border border-white/10 bg-white/5 p-6",
                                    cardHover
                                )}
                            >
                                <TrioGlow />
                                <div className="flex items-center justify-between gap-4">
                                    <div className="text-base font-semibold">{t.witness.compTitle}</div>
                                    <div className="shrink-0">
                                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
                                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                                            {t.witness.validBadge}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 space-y-3">
                                    {/* Level row */}
                                    <div className="rounded-2xl border border-white/10 bg-zinc-950/30 p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-xs uppercase tracking-widest text-white/50">
                                                {t.witness.levels}
                                            </div>
                                            <div className="text-xs text-white/50">{t.witness.defenseLabel}</div>
                                        </div>
                                        <div className="mb-3">
                                            <CompetitionDesc lang={lang} />
                                        </div>
                                        <div className="grid grid-cols-5 gap-1">
                                            {[
                                                { key: "STONE", img: levelStoneImg, defense: "50", color: "#CCCCCC" },
                                                { key: "SILVER", img: levelSilverImg, defense: "70", color: "#7CC8FF" },
                                                { key: "GOLD", img: levelGoldImg, defense: "100", color: "#FFE08C" },
                                                { key: "RUBY", img: levelRubyImg, defense: "140", color: "#FFB9D6" },
                                                { key: "DIAMOND", img: levelDiamondImg, defense: "200", color: "#EAD9FF" },
                                            ].map((lv) => (
                                                <div key={lv.key} className="flex flex-col items-center gap-1.5">
                                                    <div className="w-16 h-16 border border-white/10 rounded-xl overflow-hidden bg-zinc-950/50 flex items-center justify-center">
                                                        <img
                                                            src={lv.img}
                                                            alt={lv.key}
                                                            className="w-full h-full object-contain"
                                                            draggable={false}
                                                        />
                                                    </div>
                                                    <div className="text-[9px] font-bold text-white/60 tracking-wide">
                                                        {lv.key}
                                                    </div>
                                                    <div
                                                        className="flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-black"
                                                        style={{
                                                            backgroundColor: `${lv.color}18`,
                                                            borderColor: `${lv.color}40`,
                                                            color: lv.color,
                                                        }}
                                                    >
                                                        ♥ {lv.defense}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] text-white/55">
                                            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5">
                                                Post / React → EXP
                                            </span>
                                            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5">
                                                EXP → Level Up
                                            </span>
                                            <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5">
                                                Level → ↑ Defense
                                            </span>
                                        </div>
                                    </div>

                                    {/* Rarity row */}
                                    <div className="rounded-2xl border border-white/10 bg-zinc-950/30 p-4">
                                        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">
                                            RARITY
                                        </div>
                                        <div className="mb-3">
                                            <RarityDesc lang={lang} />
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-4 gap-y-3 gap-x-2">
                                                {([
                                                    { label: "C", desc: "Common", badge: "#606060", accent: "#CCCCCC", stars: 0 },
                                                    { label: "U", desc: "Uncommon", badge: "#2563EB", accent: "#7CC8FF", stars: 0 },
                                                    { label: "R", desc: "Rare", badge: "#7C3AED", accent: "#D3B5FF", stars: 0 },
                                                    { label: "RR", desc: "Double Rare", badge: "#CC7A05", accent: "#FFE08C", stars: 0 },
                                                    { label: "AR", desc: "Art Rare", badge: "#059669", accent: "#B3F5D6", stars: 1 },
                                                    { label: "SR", desc: "Super Rare", badge: "#DB2777", accent: "#FFB9D6", stars: 2 },
                                                    { label: "SAR", desc: "Special Art Rare", badge: "#6D28D9", accent: "#EAD9FF", stars: 3 },
                                                    { label: "UR", desc: "Ultra Rare", badge: "#D4A109", accent: "#FFF4C7", stars: 4 },
                                                ] as const).map((r, i) => (
                                                    <div key={r.label} className="flex flex-col items-center gap-1.5">
                                                        <div
                                                            className="rounded-full px-2.5 py-[5px] text-[11px] font-black tracking-wider text-white"
                                                            style={{
                                                                backgroundColor: r.badge,
                                                                boxShadow: i > 3 ? `0 0 ${8 + i * 2}px ${r.badge}90` : undefined,
                                                                border: `${i > 4 ? 1.5 : 1}px solid ${r.accent}60`,
                                                            }}
                                                        >
                                                            {r.label}
                                                        </div>
                                                        <div
                                                            className="w-14 text-center text-[9px] font-bold leading-tight"
                                                            style={{ color: i > 4 ? r.accent : "rgba(255,255,255,0.50)" }}
                                                        >
                                                            {r.desc}
                                                        </div>
                                                        {r.stars > 0 && (
                                                            <div className="flex gap-0.5">
                                                                {Array.from({ length: r.stars }).map((_, si) => (
                                                                    <span key={si} className="text-[9px]" style={{ color: r.accent }}>★</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ── EARN / REWARD CLAIM ── */}
            <section id="earn" className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
                    <SectionTitle
                        kicker={t.earn.kicker}
                        title={t.earn.title}
                        subtitle={t.earn.subtitle}
                    />

                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {/* 카드 1: 보상 누적 — 피드 홈 이미지 1장 */}
                        <div className={classNames(
                            "relative rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 backdrop-blur flex flex-col gap-6 md:flex-row md:items-start",
                            cardHover
                        )}>
                            <TrioGlow />
                            <div className="flex-1 min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                                    {t.earn.step1Tag}
                                </div>
                                <h3 className="mt-3 text-base font-semibold tracking-tight text-white">{t.earn.step1Title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-white/70">{t.earn.step1Desc}</p>
                                <p className="mt-3 text-sm leading-relaxed text-white/50">{t.earn.step1Hint}</p>
                            </div>
                            {/* 계단식 겹침 2장 — 왼쪽 10%만 가림 */}
                            <div className="shrink-0 flex justify-center items-start">
                                <div className={classNames(
                                    "relative z-10 w-[175px] overflow-hidden rounded-2xl border border-white/15 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.8)]",
                                    cardHover
                                )}>
                                    <img
                                        src={lang === "ko" ? feedHomeKoImg : feedHomeEnImg}
                                        alt="Feed home"
                                        className="block w-full object-cover"
                                        draggable={false}
                                    />
                                </div>
                                <div className={classNames(
                                    "relative z-0 w-[170px] -ml-4 mt-10 overflow-hidden rounded-2xl border border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.6)]",
                                    cardHover
                                )}>
                                    <img
                                        src={lang === "ko" ? hiveSettingKoImg : hiveSettingEnImg}
                                        alt="Hive account setting"
                                        className="block w-full object-cover"
                                        draggable={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 카드 2: 리워드 Claim — 계단식 스크린샷 2장 */}
                        <div className={classNames(
                            "relative rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 backdrop-blur flex flex-col gap-6 md:flex-row md:items-start",
                            cardHover
                        )}>
                            <TrioGlow />
                            <div className="flex-1 min-w-0">
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
                                    <HiveIcon className="h-3 w-3" />
                                    {t.earn.step2Tag}
                                </div>
                                <h3 className="mt-3 text-base font-semibold tracking-tight text-white">{t.earn.step2Title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-white/70">{t.earn.step2Desc}</p>
                            </div>
                            {/* 계단식 겹침 2장 — 왼쪽 10%만 가림 */}
                            <div className="shrink-0 flex justify-center items-start">
                                <div className={classNames(
                                    "relative z-10 w-[175px] overflow-hidden rounded-2xl border border-white/15 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.8)]",
                                    cardHover
                                )}>
                                    <img
                                        src={lang === "ko" ? rewardClaim1KoImg : rewardClaim1EnImg}
                                        alt="Reward history"
                                        className="block w-full object-cover"
                                        draggable={false}
                                    />
                                </div>
                                <div className={classNames(
                                    "relative z-0 w-[170px] -ml-4 mt-10 overflow-hidden rounded-2xl border border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.6)]",
                                    cardHover
                                )}>
                                    <img
                                        src={lang === "ko" ? rewardClaim2KoImg : rewardClaim2EnImg}
                                        alt="Reward claim"
                                        className="block w-full object-cover"
                                        draggable={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
                    <SectionTitle
                        kicker={t.faq.kicker}
                        title={t.faq.title}
                        subtitle={t.faq.subtitle}
                    />

                    <div className="mx-auto mt-10 grid max-w-3xl gap-3">
                        {getFaqItems(lang).map((item) => (
                            <FaqItem key={item.q} q={item.q} a={item.a} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="mx-auto max-w-3xl px-4 py-20 md:px-6">
                    <div className="mb-10 text-center">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-white/40">
                            {t.contact.kicker}
                        </div>
                        <h2 className="text-2xl font-bold text-white md:text-3xl">
                            {t.contact.title}
                        </h2>
                        <p className="mt-3 text-sm text-white/60">{t.contact.subtitle}</p>
                    </div>

                    {contactStatus === "success" ? (
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
                            <div className="mb-2 text-2xl">✅</div>
                            <div className="text-base font-semibold text-white">{t.contact.successTitle}</div>
                            <p className="mt-2 text-sm text-white/60">{t.contact.successBody}</p>
                            <button
                                onClick={() => { setContactStatus("idle"); setContactForm({ email: "", subject: "", content: "" }); }}
                                className="mt-6 rounded-lg border border-white/10 bg-white/5 px-5 py-2 text-sm text-white/70 hover:bg-white/10"
                            >
                                {lang === "ko" ? "새 문의하기" : "Send another"}
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleContactSubmit} className="flex flex-col gap-5">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-white/60">{t.contact.emailLabel} *</label>
                                <input
                                    type="email"
                                    required
                                    placeholder={t.contact.emailPlaceholder}
                                    value={contactForm.email}
                                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-white/30 focus:bg-white/8"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-white/60">{t.contact.subjectLabel} *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder={t.contact.subjectPlaceholder}
                                    value={contactForm.subject}
                                    onChange={e => setContactForm(f => ({ ...f, subject: e.target.value }))}
                                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-white/30"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-white/60">{t.contact.contentLabel} *</label>
                                <textarea
                                    required
                                    rows={6}
                                    placeholder={t.contact.contentPlaceholder}
                                    value={contactForm.content}
                                    onChange={e => setContactForm(f => ({ ...f, content: e.target.value }))}
                                    className="resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-white/30"
                                />
                            </div>
                            {contactStatus === "error" && (
                                <p className="text-sm text-red-400">{t.contact.errorBody}</p>
                            )}
                            <button
                                type="submit"
                                disabled={contactStatus === "sending"}
                                className="rounded-xl bg-white px-6 py-3 text-sm font-semibold text-zinc-900 transition hover:bg-white/90 disabled:opacity-50"
                            >
                                {contactStatus === "sending" ? t.contact.sending : t.contact.submit}
                            </button>
                        </form>
                    )}
                </div>
            </section>

            {/* FOOTER */}
            <footer className="relative border-t border-white/10">
                <SectionBackdrop />
                <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                            <PercentLogo className="h-7 w-7" />
                            <div>
                                <div className="text-sm font-semibold text-white/90">
                                    Percent Map
                                </div>
                                <div className="mt-1 text-xs text-white/55">
                                    {t.footer.tagline}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
                            <div className="flex items-center gap-4 text-sm">
                                <a href="/terms" className="text-white/70 hover:text-white">
                                    Terms
                                </a>
                                <a href="/privacy" className="text-white/70 hover:text-white">
                                    Privacy
                                </a>
                                <a href="/support" className="text-white/70 hover:text-white">
                                    Support
                                </a>
                                <a href="/account-deletion" className="text-white/70 hover:text-white">
                                    Account deletion
                                </a>
                            </div>

                            <div className="h-px w-full bg-white/10 md:hidden" />

                            <div className="text-sm text-white/70">
                                Contact:{" "}
                                <a
                                    className="text-white/80 hover:text-white"
                                    href="mailto:support@percentmap.com"
                                >
                                    support@percentmap.com
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
                        <div>© {new Date().getFullYear()} Percent Map. All rights reserved.</div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
